import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styles from "./AttributeSection.module.css";
import { useMapboxSearch } from "../../../../hooks/useMapboxSearch";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { sanitizeText } from "../../../../../../utils/sanitize";
import { useFloatingMenu } from "../../../../../../hooks/useFloatingMenu";
import mapRecordsErrorToKey from "../../../../utils/recordsErrorMapper";

// Helper to merge refs
function composeRefs(...refs) {
  return (node) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") ref(node);
      else ref.current = node;
    });
  };
}

export default function MapAttributes({
  location,
  onUpdate,
  onDelete,
  inputRef,
  aiOverlay,
}) {
  const { t } = useTranslation();
  const localInputRef = useRef(null);
  const hasOverlay = Boolean(aiOverlay);
  const isRemoval = hasOverlay && Boolean(aiOverlay?.isRemoval);
  const overlayValue =
    hasOverlay && !isRemoval && aiOverlay?.value && typeof aiOverlay.value === "object"
      ? aiOverlay.value
      : null;
  const overlayName =
    overlayValue && typeof overlayValue.name === "string" ? overlayValue.name : "";
  const overlayContext =
    overlayValue && typeof overlayValue.context === "string" ? overlayValue.context : "";
  const overlaySource =
    overlayValue && typeof overlayValue.source === "string" ? overlayValue.source : "";
  const locationName = location?.name || "";
  const initialQuery = hasOverlay
    ? isRemoval
      ? ""
      : overlayName
    : locationName;

  const [query, setQuery] = useState(initialQuery);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [suggestionsType, setSuggestionsType] = useState(null); // 'nearby' | 'search' | null
  const blurTimeoutRef = useRef(null);
  
  // Use floating menu hook for positioning
  const {
    open,
    setOpen,
    refs,
    floatingProps,
    update,
  } = useFloatingMenu({
    placement: "bottom-start",
    sameWidth: true,
    exactWidth: true,
    minWidthPx: 240,
    offsetValue: 8,
    maxHeight: 320,
    strategy: "fixed",
  });
  
  // Debounce the query for search
  const debouncedQuery = useDebounce(query, 300);

  const handleDelete = useCallback(() => {
    if (hasOverlay) {
      return;
    }

    if (typeof onDelete === "function") {
      onDelete();
    }
  }, [hasOverlay, onDelete]);
  
  const {
    suggestions,
    loading,
    error,
    userLocation,
    searchSuggest,
    reverseGeocode,
    retrieveDetails,
    requestGeolocation,
    resetSessionToken,
    scheduleSessionTokenReset,
    setSuggestions,
    isMapboxConfigured,
  } = useMapboxSearch();
  const mapErrorKey = error
    ? mapRecordsErrorToKey(error, {
        fallbackKey: "records.editor.map.errors.search_failed",
      })
    : null;
  
  // Sync query with overlay or RHF location value
  useEffect(() => {
    if (hasOverlay) {
      if (isRemoval) {
        setQuery("");
      } else {
        setQuery(overlayName || "");
      }
      return;
    }
    setQuery(locationName);
  }, [hasOverlay, isRemoval, overlayName, locationName]);

  // Close suggestions when overlay is active
  useEffect(() => {
    if (hasOverlay) {
      setOpen(false);
      setSelectedIndex(-1);
      setSuggestionsType(null);
      setSuggestions([]);
    }
  }, [hasOverlay, setOpen, setSuggestions]);
  
  // Trigger search when debounced query changes
  useEffect(() => {
    if (
      !hasOverlay &&
      open &&
      debouncedQuery.length >= 2 &&
      suggestionsType === 'search'
    ) {
      try {
        const maybePromise = searchSuggest(debouncedQuery, userLocation);
        if (maybePromise && typeof maybePromise.then === "function") {
          maybePromise.catch((error) => {
            console.error("Location search failed:", error);
          });
        }
      } catch (error) {
        console.error("Location search failed:", error);
      }
    }
  }, [
    debouncedQuery,
    open,
    suggestionsType,
    searchSuggest,
    userLocation,
    hasOverlay,
  ]);
  
  // Update floating position when suggestions change
  useEffect(() => {
    if (hasOverlay) {
      return;
    }
    update?.();
  }, [suggestions.length, loading, update, hasOverlay]);
  
  // Cleanup blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);
  
  // Handle input focus to show nearby places
  const handleFocus = useCallback(async () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    if (hasOverlay) {
      return;
    }

    setOpen(true);
    setSelectedIndex(-1);
    
    // If query is empty, show nearby places
    if (query === "" || query === location?.name) {
      if (userLocation) {
        await reverseGeocode(userLocation.lng, userLocation.lat);
        setSuggestionsType('nearby');
      } else {
        // Try to get location first
        const coords = await requestGeolocation();
        if (coords) {
          await reverseGeocode(coords.lng, coords.lat);
          setSuggestionsType('nearby');
        }
      }
    }
  }, [
    hasOverlay,
    query,
    location?.name,
    userLocation,
    reverseGeocode,
    requestGeolocation,
    setOpen,
  ]);
  
  // Handle input change
  const handleInputChange = useCallback((e) => {
    if (hasOverlay) {
      return;
    }

    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);
    
    if (value.length >= 2) {
      setSuggestionsType('search');
      setOpen(true);
      // Search will be triggered by the useEffect watching debouncedQuery
    } else if (value === "") {
      // Show nearby places again when input is cleared
      setSuggestionsType('nearby');
      setOpen(true);
      if (userLocation) {
        reverseGeocode(userLocation.lng, userLocation.lat);
      } else {
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
      setSuggestionsType(null);
      // Keep open to show empty state
    }
  }, [hasOverlay, userLocation, reverseGeocode, setSuggestions, setOpen]);
  
  // Handle suggestion selection
  const handleSelect = useCallback(async (suggestion) => {
    if (hasOverlay) {
      return;
    }

    try {
      let locationData = suggestion;
      
      // If it's a search suggestion, we need to retrieve full details
      if (suggestionsType === 'search' && suggestion.mapbox_id) {
        locationData = await retrieveDetails(suggestion.mapbox_id);
      } else if (suggestionsType === 'search') {
        // Transform search suggestion to location format
        // Ensure address is a string, not an object
        const addressName = typeof suggestion.address === 'object' 
          ? JSON.stringify(suggestion.address) 
          : suggestion.address;
          
        locationData = {
          name: suggestion.name || addressName || '',
          lat: null, // Will be filled by retrieveDetails
          lng: null,
          placeId: suggestion.mapbox_id,
          source: "mapbox",
          context: suggestion.place_formatted || suggestion.full_address || '',
        };
        
        if (suggestion.mapbox_id) {
          const details = await retrieveDetails(suggestion.mapbox_id);
          locationData.lat = details.lat;
          locationData.lng = details.lng;
        }
      }
      
      // Update parent component with new location data
      onUpdate("location", locationData);
      
      // Update local state
      setQuery(locationData.name);
      setOpen(false);
      setSuggestionsType(null);
      setSuggestions([]);
      
      // Reset session token after selection
      resetSessionToken();
    } catch (error) {
      console.error('Failed to select location:', error);
    }
  }, [
    hasOverlay,
    suggestionsType,
    retrieveDetails,
    onUpdate,
    resetSessionToken,
    setSuggestions,
    setOpen,
  ]);
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (hasOverlay) {
      return;
    }

    if (!open || suggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        // Allow other keys to propagate normally for text input
        break;
    }
  }, [hasOverlay, open, suggestions, selectedIndex, handleSelect, setOpen]);
  
  // Handle clearing the input
  const handleClear = useCallback(() => {
    if (hasOverlay) {
      return;
    }

    setQuery("");
    onUpdate("location", null);
    setSuggestions([]);
    setSuggestionsType(null);
    setOpen(true); // Keep open to show nearby again
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    const focusTarget = inputRef?.current ? inputRef : localInputRef;
    focusTarget.current?.focus();
  }, [hasOverlay, onUpdate, setSuggestions, inputRef, localInputRef, setOpen]);
  
  // Handle blur with delay to allow clicking suggestions
  const handleBlur = useCallback(() => {
    if (hasOverlay) {
      return;
    }

    // Clear any existing timeout before setting a new one
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    
    blurTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      setSelectedIndex(-1);
    }, 200);
    // Schedule session token reset after user stops interacting
    scheduleSessionTokenReset();
  }, [hasOverlay, scheduleSessionTokenReset, setOpen]);
  
  // Render loading state
  const renderLoadingState = () => (
    <div className={styles.locationSuggestion} style={{ cursor: 'default' }}>
      <span style={{ color: 'var(--text-secondary)' }}>
        {t("records.editor.map.searching", "Searching...")}
      </span>
    </div>
  );
  
  // Render error state
  const renderErrorState = () => (
    <div className={styles.locationSuggestion} style={{ cursor: 'default' }}>
      <span style={{ color: 'var(--danger)' }}>
        {t(mapErrorKey || "records.editor.map.errors.search_failed", {
          defaultValue: "Unable to search locations. Please try again.",
        })}
      </span>
    </div>
  );
  
  // Render no results state
  const renderNoResults = () => (
    <div className={styles.locationSuggestion} style={{ cursor: 'default' }}>
      <span style={{ color: 'var(--text-secondary)' }}>
        {t("records.editor.map.no_results", "No locations found")}
      </span>
    </div>
  );
  
  // Render suggestion item
  const renderSuggestion = (suggestion, index) => {
    const isSelected = index === selectedIndex;
    const isCurrentLocation = suggestion.source === 'manual';
    
    return (
      <div
        key={suggestion.placeId || `location-${index}`}
        id={`location-suggestion-${index}`}
        className={styles.locationSuggestion}
        onClick={() => handleSelect(suggestion)}
        onMouseEnter={() => setSelectedIndex(index)}
        role="option"
        aria-selected={isSelected}
        style={{
          background: isSelected ? 'var(--background-hover)' : undefined,
        }}
      >
        {isCurrentLocation ? (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v4" />
            <path d="M12 18v4" />
            <path d="M22 12h-4" />
            <path d="M6 12H2" />
          </svg>
        ) : (
          <span style={{ fontSize: '16px' }}>📍</span>
        )}
        <div style={{ textAlign: 'left', flex: 1 }}>
          <div>
            {isCurrentLocation
              ? t("records.editor.map.current_location", "Current location")
              : sanitizeText(suggestion.name) ||
                sanitizeText(suggestion.address) ||
                ""}
          </div>
          {suggestion.context && (
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {isCurrentLocation
                ? t("records.editor.map.current_location_context", "Your current location")
                : sanitizeText(suggestion.context)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const overlayBanner = hasOverlay ? (
    <div
      className={isRemoval ? styles.overlayRemovalBanner : styles.overlayBanner}
      role="status"
      aria-live="polite"
      id="location-ai-overlay"
      data-testid="map-attributes-ai-overlay"
    >
      {isRemoval ? (
        locationName ? (
          <>
            {t("records.editor.map.ai.remove_named", {
              location: sanitizeText(locationName),
              defaultValue: `AI will remove "${sanitizeText(locationName)}".`,
            })}
          </>
        ) : (
          t("records.editor.map.ai.clear_location", "AI will clear the location.")
        )
      ) : (
        <>
          {overlayName
            ? (
                <>
                  {t("records.editor.map.ai.set_named", {
                    location: sanitizeText(overlayName),
                    defaultValue: `AI will set the location to "${sanitizeText(overlayName)}".`,
                  })}
                </>
              )
            : t("records.editor.map.ai.update_location", "AI will update the location.")}
          {overlaySource && (
            <span style={{ marginLeft: 4, color: "var(--text-tertiary)" }}>
              ({sanitizeText(overlaySource)})
            </span>
          )}
          {overlayContext && (
            <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
              {sanitizeText(overlayContext)}
            </div>
          )}
        </>
      )}
    </div>
  ) : null;

  if (!isMapboxConfigured) {
    return (
      <div
        className={`${styles.section} ${styles.active}`}
        data-ai-overlay-active={hasOverlay || undefined}
      >
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <svg
              className={styles.icon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
            <span>{t("records.editor.map.title", "Location")}</span>
          </div>
          <button
            className={styles.deleteButton}
            onClick={handleDelete}
            aria-label={t(
              "records.editor.map.remove_attributes_aria",
              "Remove location attributes"
            )}
            disabled={hasOverlay}
            type="button"
            title={t(
              "records.editor.map.remove_attributes_aria",
              "Remove location attributes"
            )}
          >
            ×
          </button>
        </div>
        {overlayBanner}
        {!isRemoval && (
          <div className={styles.formGroup}>
            <p style={{ color: "var(--text-secondary)", fontStyle: "italic" }}>
              {t(
                "records.editor.map.not_configured",
                "Location search is not configured. Please add REACT_APP_MAPBOX_TOKEN to your environment."
              )}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${styles.section} ${styles.active} ${
        open && !hasOverlay ? styles.showingSuggestions : ""
      }`}
      data-ai-overlay-active={hasOverlay || undefined}
    >
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <svg
            className={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span>{t("records.editor.map.title", "Location")}</span>
        </div>
        <button
          className={styles.deleteButton}
          onClick={handleDelete}
          aria-label={t(
            "records.editor.map.remove_attributes_aria",
            "Remove location attributes"
          )}
          disabled={hasOverlay}
          type="button"
          title={t(
            "records.editor.map.remove_attributes_aria",
            "Remove location attributes"
          )}
        >
          ×
        </button>
      </div>

      {overlayBanner}

      {!isRemoval && (
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="location-search">
            {hasOverlay
              ? t("records.editor.map.previewed_label", "Previewed location")
              : t("records.editor.map.search_label", "Search for a location")}
          </label>
          <div className={styles.locationInputContainer}>
            <input
              ref={composeRefs(inputRef, localInputRef, refs.setReference)}
              type="text"
              id="location-search"
              className={styles.input}
              placeholder={
                hasOverlay
                  ? ""
                  : t("records.editor.map.search_placeholder", "Search for a location")
              }
              value={query}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              data-location-search-input
              role="combobox"
              readOnly={hasOverlay}
              aria-readonly={hasOverlay || undefined}
              aria-expanded={!hasOverlay && open}
              aria-controls={!hasOverlay ? "location-suggestions" : undefined}
              aria-activedescendant={
                !hasOverlay && selectedIndex >= 0
                  ? `location-suggestion-${selectedIndex}`
                  : undefined
              }
              aria-describedby={
                hasOverlay ? "location-ai-overlay" : undefined
              }
            />
            {!hasOverlay && query && query !== locationName && (
              <button
                className={styles.clearButton}
                onClick={handleClear}
                type="button"
                aria-label={t("records.editor.map.clear_aria", "Clear location")}
              >
                ×
              </button>
            )}
            {!hasOverlay && open && (
              <div
                ref={refs.setFloating}
                id="location-suggestions"
                className={`${styles.locationSuggestions} ${
                  open ? styles.active : ""
                }`}
                role="listbox"
                style={floatingProps.style}
                data-state={open ? "open" : "closed"}
              >
                {loading && renderLoadingState()}
                {!loading && error && renderErrorState()}
                {!loading && !error && suggestions.length === 0 && query.length >= 2 && renderNoResults()}
                {!loading && !error &&
                  suggestions.map((suggestion, index) =>
                    renderSuggestion(suggestion, index)
                  )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
